'use client';

import { useQuery } from '@tanstack/react-query';
import {
  EventComplete,
  FetchClaimDetailResponseBody,
  ListEventsResponseBody,
  ListEventsPaginatedResponseBody,
} from 'bff';
import { Activity, useEvent } from 'shared-components';
import { WaitForQuery } from './WaitForQuery';
import es from 'date-fns/locale/es';
import { customFormatDistance } from 'utils';
import { UserType } from 'database';

export const buildListEventsQueryKey = (resourceId: string) =>
  `list-events-${resourceId}`;

export const ActivityTab = ({
  claim,
  initialData,
  mountEvent,
  fetchEvents,
  id,
  httpClient,
  token,
  portal,
  userType,
  path,
}: {
  claim?: FetchClaimDetailResponseBody;
  initialData: ListEventsResponseBody | ListEventsPaginatedResponseBody;
  mountEvent: NonNullable<Parameters<typeof useEvent>[1]>[number];
  fetchEvents: () => Promise<
    ListEventsResponseBody | ListEventsPaginatedResponseBody
  >;
  id: string;
  httpClient: unknown;
  token?: string;
  portal?: boolean;
  userType?: UserType;
  path?: string;
}) => {
  const query = useQuery([buildListEventsQueryKey(id)], () => fetchEvents(), {
    initialData,
  });

  if (portal) {
    useEvent(httpClient as any, [mountEvent], '/portal', token);
  } else {
    useEvent(httpClient as any, [mountEvent]);
  }

  const filteredEvents = (events: EventComplete[]) => {
    return events
      .filter(
        (event) =>
          !(
            event.type === 'claim_comment_created' &&
            userType === 'inhouse_lawyer'
          ),
      )
      .filter((event) => {
        if (event.type === 'claim_updated') {
          console.log('event', event);
          const diffs = Activity.computeEventDiffs(event.metadata.diff);
          return diffs.length > 0;
        }
        return true;
      })
      .filter((event) => {
        if (event.type === 'sinister_external_status_updated') {
          if (!claim?.external_claim_id) return false;

          const compareBaseExternalId =
            claim?.external_claim_id?.substring(
              0,
              claim?.external_claim_id?.lastIndexOf('-') + 1,
            ) ===
            event.metadata?.external_complaint_id?.substring(
              0,
              event.metadata?.external_complaint_id?.lastIndexOf('-') + 1,
            );

          return compareBaseExternalId;
        }
        return true;
      })
      .filter((event) => {
        if (
          event.type === 'pending_complaint_creation_failed' ||
          event.type === 'external_complaint_selection_failed'
        ) {
          const metadataClaimId = event.metadata?.claims.find(
            (metadataClaim) => metadataClaim.id === claim?.id,
          );
          if (!metadataClaimId) return false;

          return metadataClaimId.ref_id === claim?.ref_id;
        }

        return true;
      });
  };

  return (
    <WaitForQuery query={query}>
      {({ events }) => (
        <Activity userType={userType}>
          {filteredEvents(events).map((event, index) => (
            <Activity.Item
              index={index}
              event={event}
              eventsCount={events.length}
              key={event.id}
            >
              <Activity.Content>
                <Activity.Header eventType={event.type}>
                  <Activity.TriggeredInfo event={event} path={path} />
                  <Activity.TimeAgo className='mt-0'>
                    <span className='text-end'>
                      {customFormatDistance(
                        new Date(),
                        new Date(event.created_at),
                        {
                          locale: es,
                          justDate: true,
                        },
                      )}
                    </span>
                    <span className='text-stone-400 text-end'>
                      {customFormatDistance(
                        new Date(),
                        new Date(event.created_at),
                        {
                          locale: es,
                          justSuffix: true,
                        },
                      )}
                    </span>
                  </Activity.TimeAgo>
                </Activity.Header>
              </Activity.Content>
            </Activity.Item>
          ))}
        </Activity>
      )}
    </WaitForQuery>
  );
};
